// src/com/trans_plan/TransPlan.js
import React, { useEffect, useRef, useState } from 'react';
//import { styled } from '@mui/material/styles';
import { Button, IconButton, CircularProgress } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NearMeIcon from '@mui/icons-material/NearMe'; // 지도로 이동(특정차량)
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt'; // 관제차량 선택
import LocationOnIcon from '@mui/icons-material/LocationOn'; // 지도로 이동
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {
    ContentConainer, ContentHeader, ControlBox, CountLabel, ToolBox
} from '../../common/TopViewControl';
import { AppObject, AppStyle, AppWord, MapValue } from '../../value/AppConst';
import {
    authTableTreeRepo, comInfoListRepo, storeInfoListUsingRepo, transPlanListRepo, userInfoRepo
} from '../../value/AppModel';
import { LIST_TRANS_PLAN } from './TransPlanGql';
import { TransPlanDictionary } from './TransPlanDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import { LIST_USING_STORE_INFO } from '../store_info/StoreInfoGql';
import AutoTextField from '../../common/AutoTextField';
import { LIST_COM_INFO } from '../com_info/ComInfoGql';
import ValueMisc from '../../value/ValueMisc';
import VehInfoTransPosConfig from '../veh_info/VehInfoTransPosConfig';

/**
 * (TRANS_PLAN) 정보 View Controller
 * 차량관제리스트 (운송요청 배차내역 테이블)
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-11-27 15:15:49 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function TransPlan() {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const transPlanListData = useReactiveVar(transPlanListRepo);
    const [ transPlanFiltered, setTransPlanFiltered ] = useState([]);
    const [countOfRecordsShowing, setCountOfRecordsShowing] = useState(0);
    const storeInfoListUsingData = useReactiveVar(storeInfoListUsingRepo);
    const [storeOptions, setStoreOptions] = useState([{storeId:AppWord.ALL, storeName:AppWord.AllKorean}]);
    const comInfoListData = useReactiveVar(comInfoListRepo);
    const [companyOptions, setCompanyOptions] = useState([{comId:AppWord.ALL, comName:AppWord.AllKorean}]);
    const [openTransPosConfig, setOpenTransPosConfig] = useState(false);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const checkAuth = useAuthorizationCheck();
    const [searchText, setSearchText] = useState('');
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);
    const duplicateRef = useRef({}); // 지도에 나타나지 않는 차량에 나타나는 차량 매핑.
    const mapVehRef = useRef({}); // 지도에 나타나는 차량만 모음.

    // ##### Call GraphQL to get List #####
    const [getTransPlanList, responseList] = useLazyQuery(LIST_TRANS_PLAN, {
        // 목록은 서버에서 이미 운송사, 점포, 홈플러스 등의 사용자 구분하여 출력.
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: TransPlanDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getStoreInfoListUsing, responseStoreList] = useLazyQuery(LIST_USING_STORE_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetStoreList(data, option)},
		onError: (error) => addAlert({title: "점포목록 조회 오류", message: [error.message]})
    });
    const [getComInfoList, responseComList] = useLazyQuery(LIST_COM_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetComList(data, option)},
        onError: (error) => addAlert({title: '운송사 목록 조회 오류', message: [error.message]})
    });

    const getTransPlanListNow = () => {
        getTransPlanList();
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        let diff = new Date().getTime()/1000 - transPlanListData.when;
        if(diff > 300) getTransPlanListNow();

        if(!sessionInfo.user.isStoreUser()) {
            diff = new Date().getTime()/1000 - storeInfoListUsingData.when;
            if(diff > 300) getStoreInfoListUsing();

            if(!sessionInfo.user.isTransCompanyUser()) {
                getComInfoList();
            }
        }
    }, []);

    // 점포목록에 {storeId:AppWord.ALL, storeName:AppWord.AllKorean} 추가
    useEffect(()=>{
        if(storeInfoListUsingData.records.length > 0) {
            const options = storeInfoListUsingData.records.map((store) => {
                return {storeId:store.storeId, storeName:store.storeName + ` [${store.storeId}]`};
            });
            options.unshift({storeId:AppWord.ALL, storeName:AppWord.AllKorean});
            setStoreOptions(options);
        }
    }, [storeInfoListUsingData]);

    // 운송사목록에 {comId:AppWord.ALL, comName:AppWord.AllKorean} 추가
    useEffect(()=>{
        if(comInfoListData.records.length > 0) {
            const options
            = comInfoListData.records
            .filter((com) => com.useYn === 'Y')
            .sort((a,b) => a.comName.localeCompare(b.comName))
            .map((com) => {
                return {comId:com.comId, comName:com.comName + ` [${com.comId}]`};
            });
            options.unshift({comId:AppWord.ALL, comName:AppWord.AllKorean});
            setCompanyOptions(options);
        }
    }, [comInfoListData]);

    // 관제리스트 필터링.
    useEffect(()=>{
        // filter가 변경되면 다시 필터링
        const filter = transPlanListData.filter;
        const records = transPlanListData.records;
        const filtered = records.filter((record) => {
            if(filter.storeId && record.storeId !== filter.storeId) return false;
            if(filter.comId && record.comId !== filter.comId) return false;
            return true;
        });
        setTransPlanFiltered(filtered);
    }, [transPlanListData]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.transPlanList) {
            const newData = {...transPlanListData}; // filter 때문에. filter:{storeId, comId}
            newData.when = new Date().getTime()/1000;
            newData.records = data.transPlanList;
            transPlanListRepo(newData);

            if(!sessionInfo.user.isTransCompanyUser()) {
                // comId, custId 다른 경우 지도에 표시하지 않으므로 지도 넘어갈 때 지도에 나타나는 것을 찾아 갈 수 있게 미리 준비.
                // 운송사는 하지 않아도 됨.
                const dup = {};
                const mapVeh = {};
                const posMap = {}; // 이름과 위치로 같은 차량임을 판단.
                const listHavingPosition = data.transPlanList.filter((v) => v.lat > MapValue.Korea.South && v.lat < MapValue.Korea.North && v.lon > MapValue.Korea.West && v.lon < MapValue.Korea.East);
                listHavingPosition.forEach((v) => {
                    if(v.comId === v.custId) {
                        mapVeh[v.vehInfoSeq] = v;
                        posMap[`${v.vehPlates}-${v.lat}-${v.lon}`] = v.vehInfoSeq;
                    }
                });
                // again to find duplicate
                listHavingPosition.forEach((v) =>{
                    if(v.comId !== v.custId) {
                        const key = `${v.vehPlates}-${v.lat}-${v.lon}`;
                        if(posMap[key]) {
                            dup[v.vehInfoSeq] = posMap[key];
                        }
                    }
                });
                duplicateRef.current = dup;
                mapVehRef.current = mapVeh;
            }
        }
    };

    const onCompleteGetStoreList = (data, clientOption) => {
        if(data.storeInfoListUsing) {
            storeInfoListUsingRepo({when:new Date().getTime()/1000, records:data.storeInfoListUsing});
        }
    };

    const onCompleteGetComList = (data, option) => {
        if(data.comInfoList) {
            comInfoListRepo({when:new Date().getTime()/1000, records:data.comInfoList});
        }
    };

    const onStoreSelected = (id, value) => {
        const newData = {...transPlanListData};
        newData.filter.storeId = value===AppWord.ALL ? null : value;
        transPlanListRepo(newData);
    };

    const onChangeStoreInputText = (e, value, reason) => {
        if(reason === 'clear' || (reason === 'input' && value === '')) {
            const newData = {...transPlanListData};
            newData.filter.storeId = null;
            transPlanListRepo(newData);
        }
    };

    const onCompanySelected = (id, value) => {
        const newData = {...transPlanListData};
        newData.filter.comId = value===AppWord.ALL ? null : value;
        transPlanListRepo(newData); 
    };

    const onChangeCompanyInputText = (e, value, reason) => {
        if(reason === 'clear' || (reason === 'input' && value === '')) {
            const newData = {...transPlanListData};
            newData.filter.comId = null;
            transPlanListRepo(newData);
        }
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 60) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotInOneMinute, {variant:'info', autoHideDuration:2000});
        }
        else getTransPlanListNow();
    };

    /**
     * 관제차량 선택을 위해 팝업창 띄우기. (용차가 아닌 목록. dcCd or comId로 선택해서 조회)
     */
    const onClickSetObservation = () => {
        setOpenTransPosConfig(true);
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onClickOpenMap = () => {
        const mapUrl = ValueMisc.makeMapUrl(); // No parameter
        window.open(mapUrl, AppWord.MAP_WINDOW_NAME);
    };

    const onDoubleClickRow = (record) => {
        if(record.lat > MapValue.Korea.South && record.lat < MapValue.Korea.North && record.lon > MapValue.Korea.West && record.lon < MapValue.Korea.East) {
            enqueueSnackbar('지도로 이동합니다.', {variant:'info', autoHideDuration:2000});

            let veh;
            if(!sessionInfo.user.isTransCompanyUser()) {
                // find real vehInfoSeq
                if(duplicateRef.current[record.vehInfoSeq]) {
                    veh = mapVehRef.current[duplicateRef.current[record.vehInfoSeq]];
                    if(!veh) {
                        enqueueSnackbar('지도에 표시되지 않는 차량입니다.', {variant:'info', autoHideDuration:2000});
                        return;
                    }
                }
                else veh = record;
            }
            else {
                veh = record;
            }

            const mapUrl = ValueMisc.makeMapUrl({
                    job: MapValue.MapInitEvent.traceVehicle,
                    vehInfoSeqToTrace:veh.vehInfoSeq,
                    vehPlates:veh.vehPlates,
                    vehAlias:veh.vehPlates
                });
            window.open(mapUrl, AppWord.MAP_WINDOW_NAME);
        }
        else {
            addAlert({
                title: '위치 없음',
                message: ['위치정보가 없습니다. 위치정보가 있는 경우만 지도로 이동할 수 있습니다.']
            });
        }
        /*

            const mapUrl = ValueMisc.getUrlBase() + '#' + AppWord.MAP_SUB_URL;
            window.open(mapUrl, AppWord.MAP_WINDOW_NAME);
        */
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.TO_MAP: onDoubleClickRow(row); break;
            default: return;
        }
    };

    const canSetObservation = checkAuth(authTableTree.tree.pr_veh_info_set_trans_pos_yn);
    //const canSetObservation = true;

    const tools = canSetObservation ? [
        {id:AppObject.command.TO_MAP, icon:<NearMeIcon color={AppStyle.mui.secondary}/>, where: (row)=>row.lat > MapValue.Korea.South && row.lat < MapValue.Korea.North && row.lon > MapValue.Korea.West && row.lon < MapValue.Korea.East},
    ] : [];

    const loadingAny
        = responseList.loading

    return(
        <ContentConainer>
            <ContentHeader>
                <ControlBox>
                    {
                        !sessionInfo.user.isStoreUser() &&
                        <>
                        <AutoTextField
                            dict={{
                                id: 'storeId',
                                label: '점포',
                                labelKey: 'storeName'
                            }}
                            value={transPlanListData.filter.storeId || AppWord.ALL}
                            onChange={onStoreSelected}
                            onInputChange={onChangeStoreInputText}
                            list={storeOptions}
                            style={{minWidth:250, marginRight: 10}}
                            disabled={responseStoreList.loading}
                        />
                        {
                            !sessionInfo.user.isTransCompanyUser() &&
                            <AutoTextField
                                dict={{
                                    id: 'comId',
                                    label: '운송사',
                                    labelKey: 'comName'
                                }}
                                value={transPlanListData.filter.comId || AppWord.ALL}
                                onChange={onCompanySelected}
                                onInputChange={onChangeCompanyInputText}
                                list={companyOptions}
                                style={{minWidth:300, marginRight: 10}}
                                disabled={responseComList.loading}
                            />
                        }
                        </>
                    }
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    />
                </ControlBox>
                <ToolBox>
                    <CountLabel count={countOfRecordsShowing} />
                    {
                        loadingAny ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    <Button
                        variant={AppStyle.mui.contained}
                        startIcon={<LocationOnIcon />}
                        onClick={onClickOpenMap}
                        style={{marginLeft:10}}
                    >
                        지도 열기
                    </Button>
                    {
                        canSetObservation &&
                        <Button
                            variant={AppStyle.mui.contained}
                            startIcon={<AddLocationAltIcon />}
                            onClick={onClickSetObservation}
                            disabled={loadingAny}
                            style={{marginLeft:10}}
                        >
                            관제차량 선택
                        </Button>}
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={TransPlanDictionary.tableProps.tableId}
                tableProps={TransPlanDictionary.tableProps}
                records={transPlanFiltered}
                search={searchText}
                tools={tools}
                onClickTool={onClickToolOnRow}
                onRowDoubleClicked={onDoubleClickRow}
                onChangeCountToDisplay={setCountOfRecordsShowing}
            />
            <VehInfoTransPosConfig
                open={openTransPosConfig}
                onClose={()=>setOpenTransPosConfig(false)}
            />
        </ContentConainer>
    );
}
